// src/encounter/vaccine-add-dialog.tsx
// Self-contained "+ Vaccine" picker for the A/P canvas (PROD-1227, doctor request flow). Mirrors the
// other coded *AddDialogs: owns its OWN search hook (useVaccinationSearch) — the host wires only
// { providerId(unused here), orderSeq, onConfirm, onCancel }. Two steps inside one DS Modal:
//   1) search a DDC vaccine (SearchValueSets2) → pick,
//   2) the VaccinationAdminEditor in REQUEST mode (isAdministration=false; the "Administration request
//      for nurse" toggle defaults ON, so the doctor requests and the nurse fills lot/etc. later).
// On editor save it builds a 'vaccination' CodedOrderPayload carrying the full wire entry and emits
// { planOrder, payload } → the view's onAttachOrder appends it as a per-Dx canvas order (which then
// round-trips through to-/from-patient-assessment). encounter/subject/performer on the entry are
// placeholders here — toPatientAssessment.buildVaccination re-stamps them at save.
import { useMemo, useState } from 'react';
import { Modal, Button, TextField } from '@scripsteam/scrips-signal-ds';
import { DiagnosisMultiSelect, type DiagnosisOption } from './diagnosis-multi-select';
import type { PlanOrder } from '@scripsteam/scrips-signal-ds';
import { useVaccinationSearch, type VaccineValueSet } from '@/shared/services/use-vaccination-search';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { VaccinationAdminEditor, type VaccinationDraft } from './vaccination-admin-editor';
import { vaccinationDraftToEntry } from './lib/to-vaccination-entry';
import { AP_DIALOG_LABELS } from './ap-dialog-label-style';
import type { CodedOrderPayload } from './lib/order-payload';
import type { ImmunizationCoding } from '@/types/patient-chart-api';
import { needsCatalogueLookup, withCatalogueDefaults } from './lib/vaccine-catalogue-defaults';

/** The {planOrder, payload} the view forwards to onAttachOrder. */
export interface VaccineOrder {
  planOrder: PlanOrder;
  payload: CodedOrderPayload;
  /** Every linked diagnosis, in canvas order — dxIds[0] is the nesting parent (PROD-1801). */
  dxIds: string[];
}

export interface VaccineAddDialogProps {
  /** The encounter's diagnoses for the DIAGNOSIS (n) * block (PROD-1801). */
  dxOptions: DiagnosisOption[];
  /** Pre-ticked diagnoses — the canvas seeds the Dx the picker was opened from. */
  initialDxIds?: readonly string[];
  /** Distinguishes ids across repeated attaches in one session (caller increments). */
  orderSeq?: number;
  /**
   * Pre-selected vaccine (seeded from a dragged panel item — PROD-1419). When present the dialog
   * skips the search step and opens straight in the request editor, so a dragged vaccine needs NO
   * manual re-search. Flutter: assessment_plan_screen.dart:130 addItemInApSection opens the Add
   * dialog with the dragged item.
   */
  initialItem?: ImmunizationCoding;
  /**
   * Editing an existing order (PROD-1433): seed the admin editor's fields from the saved entry
   * (lot / expiry / site / route / dose / …). Opens straight in the editor pre-filled.
   */
  initialDraft?: Partial<VaccinationDraft>;
  /**
   * Editing an existing order (PROD-1433): the saved vaccine value-set (carries the saved `route` +
   * unitOfMeasurement). Takes precedence over `initialItem` so the editor rebuilds the saved route on
   * submit (`codingToValueSet` would leave route `[]` and drop it — Codex High).
   */
  initialValueSet?: VaccineValueSet;
  editing?: boolean;
  onConfirm: (order: VaccineOrder) => void;
  onCancel: () => void;
  /**
   * Destructive DELETE — the red trash the Figma order-dialog frames put bottom-right. Editing only;
   * the host passes its existing order-removal callback.
   */
  onDelete?: () => void;
  /**
   * AUTHORING an order set (PROD-2714): no encounter, so no DIAGNOSIS block, and the editor hides
   * the visit's own fields (see VaccinationAdminEditor `authoring`).
   */
  authoring?: boolean;
}

/** Stable order id from the picked vaccine code + the session sequence (no random/clock source). */
function makeOrderId(code: string | undefined, seq: number): string {
  return `vax-${code ?? 'x'}-${seq}`;
}

/**
 * Adapt a bare coding (a FAVOURITE row, which normalises to `{code, display, system}` and drops
 * route/uom) → the VaccineValueSet the editor needs.
 *
 * `route`/`unitOfMeasurement` are genuinely unknown here, so they start empty and are filled by the
 * by-code catalogue lookup below — Flutter does the same thing via `fetchSelectedVaccinationData`
 * (vaccination_add_widget.dart:159-172 → `setRouteData(r.first.route, r.first.unitOfMeasurement)`).
 *
 * The `system` is passed through UNCHANGED (PROD-1819). It used to default to `'DDC'`, which asserted
 * a coding system nobody had verified: a CVX-sourced row arrived downstream *labelled* DDC while
 * carrying a CVX code, so it looked like a legitimate product that merely had no stock. That is
 * precisely why the empty LotPicker read as missing inventory rather than a wrong code. The coding
 * system is org configuration (GetUserDataToken → `resourceBasedIntegration`) — never a literal here.
 */
function codingToValueSet(c: ImmunizationCoding): VaccineValueSet {
  const system = (c.system ?? '').trim();
  return {
    valueCoding: {
      code: (c.code ?? '').trim(),
      display: (c.display ?? c.description ?? c.Definition ?? c.code ?? '').trim(),
      system,
    },
    unitOfMeasurement: null,
    route: [],
  };
}

export function VaccineAddDialog({ orderSeq = 0, dxOptions, initialDxIds, initialItem, initialDraft, initialValueSet, editing, onConfirm, onCancel, onDelete, authoring = false }: VaccineAddDialogProps) {
  const [query, setQuery] = useState('');
  const [dxIds, setDxIds] = useState<string[]>(() => [...(initialDxIds ?? [])]);
  // PROD-2896: the no-diagnosis save refusal, shown above the actions until a diagnosis is ticked.
  const [dxError, setDxError] = useState<string | undefined>();
  const [picked, setPicked] = useState<VaccineValueSet | null>(() =>
    // Edit: the saved value-set (with route/uom) wins so submit rebuilds the saved route; add: the
    // dragged coding (route filled in the editor); else null → search step.
    initialValueSet ?? (initialItem ? codingToValueSet(initialItem) : null),
  );
  const debounced = useDebounce(query, 300);
  const { data: results = [], isFetching } = useVaccinationSearch(debounced, !picked);

  /**
   * BY-CODE CATALOGUE LOOKUP — Flutter's `fetchSelectedVaccinationData`
   * (vaccination_add_widget.dart:159-172): search the DDC value-set for the picked code and adopt its
   * `route` + `unitOfMeasurement`.
   *
   * Needed because the three ways in carry different amounts of data (PROD-1819):
   *   • rail search  → a full VaccineValueSet (route + uom present) → this stays disabled
   *   • FAVOURITE    → normalises to `{code, display, system}` only → route/uom must be looked up
   *   • edit         → the saved entry rebuilds route from what was persisted
   * Without it a favourite-sourced vaccine opened the editor with NO route and no admin/dose unit,
   * which is the same defect class as the rail's wrong code system — just one step further along.
   *
   * `SearchValueSets2` accepts a code as its SearchText (verified on dev: `SearchText=0036-103402-0511`
   * returns the VAXIGRIP row), so the existing search hook doubles as the by-code lookup — no new
   * endpoint. Mirrors how the medication dose dialog uses `useMedicationValueSet` (PROD-1434).
   */
  const pickedCode = picked?.valueCoding.code?.trim() ?? '';
  const wantsLookup = needsCatalogueLookup(picked) && pickedCode.length > 0;
  const { data: catalogue } = useVaccinationSearch(pickedCode, wantsLookup);
  // `picked` stays exactly what was picked; the editor gets it with the catalogue's gaps filled.
  // The user's own route/unit choices live in the editor's form, so re-deriving never clobbers them.
  // `catalogue` is TanStack Query data: the same reference until the query refetches (structural
  // sharing), so this memo recomputes on a new pick or new catalogue rows only, not on every render.
  const vaccine = useMemo(() => withCatalogueDefaults(picked, catalogue), [picked, catalogue]);

  function handleEditorSave(draft: VaccinationDraft) {
    // PROD-2896, do as Flutter: Save with no diagnosis ticked is REFUSED, on add and on edit, with
    // Flutter's own words. The vaccine widget builds through the shared base, whose Save handler stops
    // before getData() when selectedDiagnosis is empty (base_ap_item_add_widget.dart:295-299) — so
    // Flutter never saves, unlinks or removes the vaccine. Without this the edit kept the old links
    // (replaceOrderKeepingLinks min-1) and silently ignored the untick. Authoring has no picker.
    if (!authoring && dxIds.length === 0) {
      setDxError('No diagnosis is selected.');
      return;
    }
    // ctx placeholders — buildVaccination re-stamps encounter/subject/performer + parentId at save.
    const entry = vaccinationDraftToEntry(draft, { encounter: '', subject: '', performer: '' });
    const planOrder: PlanOrder = {
      id: makeOrderId(draft.vaccine.valueCoding.code, orderSeq),
      type: 'vaccination',
      label: draft.vaccine.valueCoding.display,
      // dxIds empty — the host attaches the target Dx when it appends the order (v2 PROD-1422).
      dxIds: [],
    };
    onConfirm({ planOrder, payload: { kind: 'vaccination', entry }, dxIds });
  }

  return (
    <Modal open onClose={onCancel} size="lg">
      {/* The dialog's accessible name — no painted title, same pattern as the other order dialogs. */}
      {/* Covers the SEARCH step's own label; the editor below carries the scope itself too. */}
      <div role="group" aria-label={editing ? 'Edit vaccination' : 'Add vaccination'} className={AP_DIALOG_LABELS}>
      {!vaccine ? (
        <div className="font-sans" data-slot="vaccine-search">
          <TextField
            id="vaccine-search"
            label="Search vaccine"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a vaccine name…"
            autoFocus
          />
          {/* maxHeight 320: no DS token (LIST — arbitrary px). */}
          <div className="flex flex-col gap-2 mt-3 max-h-80 overflow-y-auto scrollbar-hide">
            {isFetching && results.length === 0 && (
              <p className="m-0 text-size-sm text-text-hint">Searching…</p>
            )}
            {!isFetching && debounced && results.length === 0 && (
              <p className="m-0 text-size-sm text-text-hint">No vaccines found.</p>
            )}
            {results.map((v) => (
              // DS-only (PROD-1369): result rows are tertiary DS Buttons (full-width, left-aligned),
              // not raw `<button>` — same pattern as the flag-add picker.
              <Button
                key={v.valueCoding.code}
                variant="tertiary"
                size="sm"
                onClick={() => setPicked(v)}
                fullWidth
                className="justify-start"
              >
                {v.valueCoding.display}
              </Button>
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="tertiary" size="sm" onClick={onCancel}>Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <VaccinationAdminEditor
          // DIAGNOSIS goes BELOW the vaccine header, not above it (31 Jul). Flutter builds header()
          // then diagnosisSelectionWidget() (base_ap_item_add_widget.dart:192,195) and our other five
          // order dialogs do the same — this was the only inverted one.
          headerSlot={
            authoring ? undefined : (
              <div className="mb-4">
                <DiagnosisMultiSelect
                  diagnoses={dxOptions}
                  selectedIds={dxIds}
                  onChange={(ids) => {
                    setDxIds(ids);
                    if (ids.length > 0) setDxError(undefined);
                  }}
                />
              </div>
            )
          }
          authoring={authoring}
          vaccine={vaccine}
          isAdministration={false}
          // Doctor requesting → the "Administration request for nurse" toggle defaults ON. On edit, the
          // saved entry's fields (initialDraft) override, so the request re-opens exactly as saved.
          initial={{ isAdministrationRequested: true, ...initialDraft }}
          // "Save", like every other A/P dialog — Flutter uses one label for all of them
          // (base_ap_item_add_widget.dart:288-290). "Add to plan" was ours alone.
          saveLabel="Save"
          saveError={dxError}
          onSave={handleEditorSave}
          // CANCEL CLOSES, always (Andrew, 1 Aug: cancelling should not reopen the vaccine search).
          // It used to fall back to the search step on an add, which made this the only dialog where
          // Cancel did not dismiss — you pressed it to leave and got another popup. The other five
          // close outright, and so does this now.
          onCancel={onCancel}
          onDelete={onDelete}
          />
        </>
      )}
      </div>
    </Modal>
  );
}
